import axios from "axios";
import Swal from "sweetalert2";
import {
  showSubscriptionExpiredPopup,
  showUpgradeRequiredPopup,
} from "@/utils/subscriptionAccess";


const getCookie = (name) => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  const key = `${name}=`;
  for (const raw of cookies) {
    const cookie = raw.trim();
    if (cookie.startsWith(key)) {
      return decodeURIComponent(cookie.slice(key.length));
    }
  }
  return null;
};

// A Portal support handoff starts a read-only, short-lived customer session.
// Do not let an optional dashboard request with a 401 discard that session and
// force the operator back to login before the support banner can load.
const isSupportHandoff = () => {
  if (typeof window === "undefined") return false;

  try {
    return (
      new URLSearchParams(window.location.search).get("supported") === "1" ||
      window.sessionStorage.getItem("tarh_support_handoff") === "1"
    );
  } catch (_) {
    return false;
  }
};

const CACHE_TTL = 30 * 1000; // 30 seconds of reuse for GET requests
const MAX_CACHE_ENTRIES = 50;
const responseCache = new Map();
let activeRequestCount = 0;
let supportAccessToken = null;

// Support tokens are intentionally memory-only. They disappear on refresh or
// tab close and cannot become a persistent customer login credential.
export const setSupportAccessToken = (token) => {
  responseCache.clear();
  supportAccessToken = typeof token === "string" && token.trim() ? token.trim() : null;
};

// Forgets the support session in this tab: the token, the marker that keeps
// 401s from redirecting, and every cached response, which holds customer data.
export const clearSupportSession = () => {
  supportAccessToken = null;
  responseCache.clear();
  try {
    window.sessionStorage.removeItem("tarh_support_handoff");
  } catch (_) {
    // Session storage may be unavailable; the token is gone either way.
  }
};

// Leaves the customer's screens for good. Loading a fresh page, rather than
// navigating inside the app, discards everything the app holds in memory about
// the customer (store, component state, pending requests).
export const leaveSupportSession = (reason = "ended") => {
  clearSupportSession();
  window.location.replace(`/support/ended?reason=${encodeURIComponent(reason)}`);
};

const emitRequestState = (phase) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("tarh:request-state", {
      detail: {
        phase,
        activeCount: activeRequestCount,
      },
    })
  );
};

const normalizeForCache = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForCache(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeForCache(value[key]);
        return acc;
      }, {});
  }
  return value;
};

const buildCacheKey = (config) => {
  const method = (config.method || "get").toLowerCase();
  const base = config.baseURL ? config.baseURL.replace(/\/+$/, "") : "";
  const urlPath = (config.url || "").replace(/^\/+/, "");
  const langHeader =
    config?.headers?.["X-Lang"] || config?.headers?.["x-lang"] || "";
  const branchHeader =
    config?.headers?.["X-Branch-ID"] || config?.headers?.["x-branch-id"] || "";
  const params =
    config.params && Object.keys(config.params).length > 0
      ? JSON.stringify(normalizeForCache(config.params))
      : "";
  return [method, `${base}/${urlPath}`, params, langHeader, branchHeader].join("::");
};

const shouldBypassCache = (config) => {
  if (!config) return true;
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") return true;
  if (config.forceRefresh) return true;
  const cacheControl =
    config.headers?.["Cache-Control"] || config.headers?.["cache-control"];
  if (cacheControl && cacheControl.includes("no-cache")) return true;
  if (config.params && config.params.nocache) return true;
  return false;
};

const enforceCacheLimit = () => {
  while (responseCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    responseCache.delete(oldestKey);
  }
};

const cacheResponse = (key, response) => {
  if (!key) return;
  responseCache.set(key, {
    timestamp: Date.now(),
    payload: {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    },
  });
  enforceCacheLimit();
};

const api = axios.create({
  // Toggle between direct backend and an obfuscated same-origin reverse-proxy path.
  // - Set `VITE_USE_EDGE_PROXY=true` to use `VITE_EDGE_ROUTE` (default: "/_r9KpQ1x7N4")
  // - Otherwise uses `VITE_API_BASE_URL` (default: "http://localhost:8000/api")
  baseURL:
    String(import.meta.env.VITE_USE_EDGE_PROXY || "").toLowerCase() === "true" ||
    String(import.meta.env.VITE_USE_EDGE_PROXY || "") === "1"
      ? import.meta.env.VITE_EDGE_ROUTE || "/_r9KpQ1x7N4"
      : import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api",
  withCredentials: true,
  timeout: 60000,
  headers: {
    "X-Lang": localStorage.getItem("lang") || "ar", // Send language preference
  },
});

// Always-on CSRF auto-retry (works even if setupInterceptors() wasn't called yet).
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    const csrfError =
      status === 422 &&
      (data?.error === "CSRF token mismatch." ||
        data?.message === "CSRF token mismatch." ||
        data?.error === "CSRF origin check failed." ||
        data?.message === "CSRF origin check failed.");

    const isCsrfCookieRequest =
      typeof err?.config?.url === "string" && err.config.url.includes("/csrf-cookie");

    if (csrfError && err?.config && !err.config.__csrfRetried && !isCsrfCookieRequest) {
      try {
        err.config.__csrfRetried = true;
        await api.get("/csrf-cookie", { forceRefresh: true });

        const xsrfToken = getCookie("XSRF-TOKEN");
        if (xsrfToken) {
          err.config.headers = err.config.headers || {};
          err.config.headers["X-XSRF-TOKEN"] = xsrfToken;
        }

        return api.request(err.config);
      } catch (_) {
        // fall through
      }
    }

    return Promise.reject(err);
  }
);

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  config.metadata = config.metadata || {};
  const currentLang = localStorage.getItem("lang") || "ar";
  config.headers = config.headers || {};
  config.headers["X-Lang"] = currentLang;
  const activeBranchId = localStorage.getItem("active_branch_id");
  if (activeBranchId) {
    config.headers["X-Branch-ID"] = activeBranchId;
  }
  if (isSupportHandoff()) {
    config.headers["X-Support-Handoff"] = "1";
  }
  if (supportAccessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${supportAccessToken}`;
  }

  if (method !== "get" && method !== "head" && method !== "options") {
    const xsrfToken = getCookie("XSRF-TOKEN");
    if (xsrfToken) {
      config.headers["X-XSRF-TOKEN"] = xsrfToken;
    }
  }

  if (!shouldBypassCache(config)) {
    const key = buildCacheKey(config);
    config.metadata.cacheKey = key;
    const cached = responseCache.get(key);
    if (cached) {
      const isExpired = Date.now() - cached.timestamp > CACHE_TTL;
      if (!isExpired) {
        config.metadata.cacheHit = true;
        config.metadata.trackRequest = false;
        return {
          ...config,
          adapter: () =>
            Promise.resolve({
              data: cached.payload.data,
              status: cached.payload.status,
              statusText: cached.payload.statusText,
              headers: cached.payload.headers,
              config,
              request: {},
            }),
        };
      }
      responseCache.delete(key);
    }
  } else if (method === "get") {
    config.metadata.skipCacheStore = true;
  } else {
    config.metadata.clearCache = true;
  }

  if (config.metadata.trackRequest === undefined) {
    config.metadata.trackRequest = true;
  }

  if (config.metadata.trackRequest) {
    activeRequestCount += 1;
    emitRequestState("start");
  }

  return config;
});

// Setup function to inject dispatch
export const setupInterceptors = (dispatch) => {
  let lastNetworkAlertAt = 0;
  const NETWORK_ALERT_COOLDOWN_MS = 15000;
  const finalizeTrackedRequest = (config) => {
    if (!config?.metadata?.trackRequest) return;
    activeRequestCount = Math.max(0, activeRequestCount - 1);
    emitRequestState("end");
  };

  api.interceptors.response.use(
    (res) => {
      const cacheKey = res?.config?.metadata?.cacheKey;
      const cacheHit = res?.config?.metadata?.cacheHit;
      const skipStore = res?.config?.metadata?.skipCacheStore;
      const method = (res?.config?.method || "get").toLowerCase();

      if (!cacheHit && !skipStore && method === "get" && cacheKey) {
        cacheResponse(cacheKey, res);
      }

      if (res?.config?.metadata?.clearCache) {
        responseCache.clear();
      }

      finalizeTrackedRequest(res?.config);

      return res;
    },
    async (err) => {
      const originalConfig = err?.config;
      const status = err?.response?.status;
      const isCanceled =
        err?.code === "ERR_CANCELED" || err?.message === "canceled";
      const isNetworkError =
        !isCanceled &&
        (err?.code === "ERR_NETWORK" ||
          err?.message === "Network Error" ||
          (!err?.response && err?.request));

      // If the JWT cookie exists but the XSRF cookie is missing/stale, the API returns 422.
      // Refresh the CSRF cookie and retry the original request once.
      const csrfError =
        status === 422 &&
        (err?.response?.data?.error === "CSRF token mismatch." ||
          err?.response?.data?.error === "CSRF origin check failed.");

      if (csrfError && err?.config && !err.config.__csrfRetried) {
        try {
          err.config.__csrfRetried = true;
          finalizeTrackedRequest(originalConfig);
          if (err.config.metadata) {
            err.config.metadata.trackRequest = true;
          }
          await api.get("/csrf-cookie", { forceRefresh: true });

          const xsrfToken = getCookie("XSRF-TOKEN");
          if (xsrfToken) {
            err.config.headers = err.config.headers || {};
            err.config.headers["X-XSRF-TOKEN"] = xsrfToken;
          }

          return api.request(err.config);
        } catch (_) {
          // Fall through to standard error handling.
        }
      }
      const currentPath = window.location?.pathname || "";
      const authPages = ["/login", "/register", "/forgot", "/reset"];
      const isPublicRegistrationPage =
        currentPath === "/register" || currentPath.startsWith("/register/");
      const requestUrl = err?.config?.url || "";

      // The server ends a support session after 15 minutes, or when Portal
      // reconnects. Leave at once rather than keep the customer's screens open
      // with requests that fail.
      if (supportAccessToken && status === 401 && !requestUrl.includes("/support/exit")) {
        leaveSupportSession("expired");
        return Promise.reject(err);
      }
      const ignoredEndpoints = [
        "/login",
        "/register",
        "/forgot-password",
        "/reset-password",
        "/user",
        "/support/consume/",
        "/subscription/status",
      ];
      const shouldSkipRedirect =
        authPages.includes(currentPath) ||
        isPublicRegistrationPage ||
        isSupportHandoff() ||
        ignoredEndpoints.some((endpoint) => requestUrl.includes(endpoint));

      if (isCanceled) {
        return Promise.reject(err);
      }

      if (isNetworkError) {
        const now = Date.now();
        const shouldShowNetworkAlert =
          !shouldSkipRedirect && now - lastNetworkAlertAt > NETWORK_ALERT_COOLDOWN_MS;

        if (shouldShowNetworkAlert) {
          lastNetworkAlertAt = now;
          Swal.fire({
            text: "Network error or server unreachable. Please check your connection and try again.",
            icon: "warning",
            iconColor: "#dc2626",
          });
        }
        return Promise.reject(err);
      } else if (status === 401 && !shouldSkipRedirect) {
        dispatch({ type: "auth/logout" });
        window.location.href = "/login";
      } else if (status === 403) {
        const reason =
          err?.response?.data?.code ||
          err?.response?.data?.reason ||
          err?.response?.data?.message ||
          "";
        const isSubscriptionExpired =
          (typeof reason === "string" &&
            reason.toLowerCase().includes("subscription_expired")) ||
          err?.response?.data?.code === "subscription_expired";
        const isPlanUpgradeRequired =
          typeof reason === "string" &&
          reason.toLowerCase().includes("plan_upgrade_required");
        const isAccountInactive =
          err?.response?.data?.code === "account_inactive" ||
          (typeof reason === "string" &&
            reason.toLowerCase().includes("account_inactive")) ||
          (typeof reason === "string" &&
            reason.toLowerCase().includes("access to your account")) ||
          (typeof reason === "string" &&
            reason.toLowerCase().includes("don't have access"));
        const isSubscriptionError =
          typeof reason === "string" &&
          reason.toLowerCase().includes("subscription");

        if (isSubscriptionExpired && !shouldSkipRedirect) {
          showSubscriptionExpiredPopup();
          dispatch({ type: "subscriptions/forceUnsubscribed" });
        } else if (isPlanUpgradeRequired && !shouldSkipRedirect) {
          showUpgradeRequiredPopup();
        } else if (
          isAccountInactive &&
          !authPages.includes(currentPath) &&
          !isPublicRegistrationPage
        ) {
          Swal.fire({
            icon: "warning",
            iconColor: "#dc2626",
            text:
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              "You don't have access to your account.",
            confirmButtonText: "OK",
          });
          dispatch({ type: "auth/logout" });
          window.location.href = "/login";
        } else if (isSubscriptionError) {
          dispatch({ type: "subscriptions/forceUnsubscribed" });
        }
      }

      if (err?.config?.metadata?.clearCache) {
        responseCache.clear();
      }

      finalizeTrackedRequest(originalConfig);

      return Promise.reject(err);
    }
  );
};

export default api;
